import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { ToastService } from './toast.service';
import { WebSocketService } from './websocket.service';
import { TicketRealtimeService, TicketUpdateEvent, TicketCommentEvent, TicketStatusChangedEvent, TicketAssignedEvent } from './ticket-realtime.service';

/**
 * Ticket Notification Service
 *
 * Listens to WebSocket ticket events and displays toast notifications
 * to all logged-in users of the organization.
 *
 * This service is initialized in the app root to ensure all ticket
 * updates are captured regardless of which page the user is on.
 *
 * Notifications include:
 * - Ticket status changes
 * - New comments from support agents
 * - Ticket assignments
 * - General ticket updates from Zoho Desk webhooks
 */
@Injectable({
  providedIn: 'root'
})
export class TicketNotificationService implements OnDestroy {
  private destroy$ = new Subject<void>();
  private isInitialized = false;

  constructor(
    private toastService: ToastService,
    private websocketService: WebSocketService,
    private ticketRealtime: TicketRealtimeService,
    private router: Router
  ) {}

  /**
   * Initialize the notification listeners
   * Should be called once when the app starts (after authentication)
   */
  initialize(): void {
    if (this.isInitialized) {
      return;
    }

    console.log('[TicketNotification] Initializing ticket notification listeners');

    // Subscribe to connection state to start listening when connected
    this.websocketService.connectionState$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(state => {
      if (state === 'authenticated') {
        this.subscribeToTicketEvents();
      }
    });

    this.isInitialized = true;
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Subscribe to all ticket-related WebSocket events
   */
  private subscribeToTicketEvents(): void {
    // Listen for ticket updates (general updates from Zoho webhook)
    this.ticketRealtime.ticketUpdates$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event) {
        this.handleTicketUpdate(event);
      }
    });

    // Listen for new comments
    this.ticketRealtime.ticketComments$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event) {
        this.handleNewComment(event);
      }
    });

    // Listen for status changes
    this.ticketRealtime.ticketStatusChanges$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event) {
        this.handleStatusChange(event);
      }
    });

    // Listen for assignment changes
    this.ticketRealtime.ticketAssignments$.pipe(
      takeUntil(this.destroy$)
    ).subscribe(event => {
      if (event) {
        this.handleAssignment(event);
      }
    });

    console.log('[TicketNotification] Subscribed to ticket events');
  }

  /**
   * Handle generic ticket update event
   */
  private handleTicketUpdate(event: TicketUpdateEvent): void {
    // Don't show duplicate notifications if we also get a more specific event
    // Check if this is just a general "update" without specific changes
    if (event.eventType === 'update' && !event.changes) {
      this.toastService.info('A support ticket has been updated', {
        title: 'Ticket Update',
        duration: 5000,
        icon: 'bi-ticket-detailed',
        action: {
          label: 'View Ticket',
          callback: () => this.navigateToTicket(event.ticketId)
        }
      });
    }
  }

  /**
   * Handle new comment event
   */
  private handleNewComment(event: TicketCommentEvent): void {
    const authorType = event.comment.authorType === 'agent' ? 'Support' : event.comment.author;
    const isPublicNote = event.comment.isPublic ? '' : ' (internal note)';

    this.toastService.info(`${authorType} added a comment to your ticket${isPublicNote}`, {
      title: 'New Comment',
      duration: 6000,
      icon: 'bi-chat-left-text',
      action: {
        label: 'View',
        callback: () => this.navigateToTicket(event.ticketId)
      }
    });
  }

  /**
   * Handle status change event
   */
  private handleStatusChange(event: TicketStatusChangedEvent): void {
    const statusText = this.getStatusDisplayText(event.newStatus);
    const icon = this.getStatusIcon(event.newStatus);
    const type = this.getStatusToastType(event.newStatus);

    this.toastService.show(`Your ticket status changed to ${statusText}`, {
      type,
      title: 'Status Update',
      duration: 6000,
      icon,
      action: {
        label: 'View Ticket',
        callback: () => this.navigateToTicket(event.ticketId)
      }
    });
  }

  /**
   * Handle ticket assignment event
   */
  private handleAssignment(event: TicketAssignedEvent): void {
    const assigneeName = event.assignee?.name || 'a support agent';

    this.toastService.info(`Your ticket has been assigned to ${assigneeName}`, {
      title: 'Ticket Assigned',
      duration: 5000,
      icon: 'bi-person-check',
      action: {
        label: 'View',
        callback: () => this.navigateToTicket(event.ticketId)
      }
    });
  }

  /**
   * Navigate to a specific ticket
   */
  private navigateToTicket(ticketId: string): void {
    this.router.navigate(['/maintenance/support-tickets', ticketId]);
  }

  /**
   * Get human-readable status text
   */
  private getStatusDisplayText(status: string): string {
    const statusMap: Record<string, string> = {
      'open': 'Open',
      'in_progress': 'In Progress',
      'resolved': 'Resolved',
      'closed': 'Closed',
      'pending': 'Pending',
      'on_hold': 'On Hold'
    };
    return statusMap[status.toLowerCase()] || status;
  }

  /**
   * Get icon for status
   */
  private getStatusIcon(status: string): string {
    const iconMap: Record<string, string> = {
      'open': 'bi-circle',
      'in_progress': 'bi-arrow-repeat',
      'resolved': 'bi-check-circle',
      'closed': 'bi-x-circle',
      'pending': 'bi-clock',
      'on_hold': 'bi-pause-circle'
    };
    return iconMap[status.toLowerCase()] || 'bi-info-circle';
  }

  /**
   * Get toast type based on status
   */
  private getStatusToastType(status: string): 'success' | 'info' | 'warning' {
    const statusLower = status.toLowerCase();
    if (statusLower === 'resolved' || statusLower === 'closed') {
      return 'success';
    }
    if (statusLower === 'on_hold' || statusLower === 'pending') {
      return 'warning';
    }
    return 'info';
  }
}
